/**
 * Deferred hero/slideshow video loader (perf 2026-06).
 * Posters paint as LCP; MP4 sources load after idle on desktop,
 * and after LCP on mobile (poster-first, then autoplay crossfade).
 * Secondary/desktop posters use data-hog-poster-* until after LCP / on select (not data-src — Hyperspeed rewrites those).
 */
(function () {
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');

  /** @type {boolean} */
  let lcpReady = false;
  /** @type {Array<() => void>} */
  const lcpWaiters = [];

  function shouldSkipVideo() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return true;
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!conn) return false;
    if (conn.saveData) return true;
    return conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g';
  }

  function isMobile() {
    return MOBILE_QUERY.matches;
  }

  /**
   * Run callback after LCP (or idle/load fallback). Mobile video waits on this.
   * @param {() => void} callback
   */
  function afterLcp(callback) {
    if (lcpReady) {
      callback();
      return;
    }
    lcpWaiters.push(callback);
  }

  function markLcpReady() {
    if (lcpReady) return;
    lcpReady = true;
    while (lcpWaiters.length) {
      const cb = lcpWaiters.shift();
      if (cb) cb();
    }
  }

  function initLcpGate() {
    if (!('PerformanceObserver' in window)) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(markLcpReady, { timeout: 4000 });
      } else {
        setTimeout(markLcpReady, 2500);
      }
      return;
    }

    try {
      const po = new PerformanceObserver(function (list) {
        if (list.getEntries().length) {
          po.disconnect();
          markLcpReady();
        }
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
      addEventListener(
        'load',
        function () {
          setTimeout(markLcpReady, 2500);
        },
        { once: true }
      );
    } catch (e) {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(markLcpReady, { timeout: 4000 });
      } else {
        setTimeout(markLcpReady, 2500);
      }
    }
  }

  /** @param {HTMLImageElement} img */
  function hydratePoster(img) {
    if (!img || img.getAttribute('data-hog-poster-hydrated') === 'true') return;
    const src = img.getAttribute('data-hog-poster-src');
    if (!src) return;

    // Never pull the other viewport's poster — keeps mobile LCP uncontested and
    // stops Hyperspeed/eager rewrites of only-desktop from mattering on phones.
    if (isMobile() && img.classList.contains('only-desktop')) return;
    if (!isMobile() && img.classList.contains('only-mobile')) return;

    const srcset = img.getAttribute('data-hog-poster-srcset');
    const sizes = img.getAttribute('data-hog-poster-sizes');
    if (srcset) img.setAttribute('srcset', srcset);
    if (sizes) img.setAttribute('sizes', sizes);
    img.setAttribute('src', src);
    img.removeAttribute('data-hog-poster-src');
    img.removeAttribute('data-hog-poster-srcset');
    img.removeAttribute('data-hog-poster-sizes');
    img.setAttribute('data-hog-poster-hydrated', 'true');
  }

  /** @param {ParentNode | null | undefined} container */
  function hydrateSlidePosters(container) {
    if (!container) return;
    container.querySelectorAll('img[data-hog-poster-src]').forEach(function (img) {
      if (img instanceof HTMLImageElement) hydratePoster(img);
    });
  }

  /** Desktop LCP: hydrate active slide's only-desktop poster immediately. */
  function hydrateActiveDesktopPoster(activeSlide) {
    if (isMobile() || !activeSlide) return;
    activeSlide.querySelectorAll('img.only-desktop[data-hog-poster-src]').forEach(function (img) {
      if (img instanceof HTMLImageElement) hydratePoster(img);
    });
  }

  /**
   * Hydrate the slide after the active one so autoplay is not a blank flash.
   * @param {Element} root
   * @param {Element | null} activeSlide
   */
  function hydrateNextSlidePosters(root, activeSlide) {
    const slides = root.querySelectorAll('slideshow-slide, [id^="Slide-"]');
    if (!slides.length) return;

    let activeIndex = 0;
    for (let i = 0; i < slides.length; i++) {
      if (slides[i] === activeSlide) {
        activeIndex = i;
        break;
      }
    }
    const next = slides[(activeIndex + 1) % slides.length];
    if (next && next !== activeSlide) hydrateSlidePosters(next);
  }

  /** @param {ParentNode} container */
  function getViewportVideo(container) {
    const videos = container.querySelectorAll('.hog-deferred-video');
    /** @type {HTMLVideoElement | null} */
    let fallback = null;
    const mobile = isMobile();

    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      if (mobile) {
        if (video.classList.contains('only-desktop')) continue;
        if (video.classList.contains('only-mobile')) return video;
      } else {
        if (video.classList.contains('only-mobile')) continue;
        if (video.classList.contains('only-desktop')) return video;
      }
      if (!fallback) fallback = video;
    }
    return fallback;
  }

  /** @param {HTMLVideoElement | null | undefined} video */
  function unloadVideo(video) {
    if (!video) return;
    video.pause();
    video.classList.remove('slide__video--ready', 'hero-slide__video--ready');
    video.removeAttribute('data-loaded');
    video.querySelectorAll('source').forEach(function (source) {
      source.removeAttribute('src');
    });
    video.load();
  }

  /** @param {HTMLVideoElement | null | undefined} video */
  function loadVideo(video) {
    if (!video || video.getAttribute('data-loaded') === 'true') return;
    if (shouldSkipVideo()) return;

    const sources = video.querySelectorAll('source[data-video-src]');
    let hasSrc = false;
    sources.forEach(function (source) {
      const url = source.getAttribute('data-video-src');
      if (url) {
        source.setAttribute('src', url);
        hasSrc = true;
      }
    });
    if (!hasSrc) return;

    video.setAttribute('data-loaded', 'true');

    video.addEventListener(
      'error',
      function () {
        video.classList.remove('slide__video--ready', 'hero-slide__video--ready');
        video.removeAttribute('data-loaded');
      },
      { once: true }
    );

    const revealVideo = function () {
      if (video.classList.contains('slide__video')) {
        video.classList.add('slide__video--ready');
      }
      if (video.classList.contains('hero-slide__video')) {
        video.classList.add('hero-slide__video--ready');
      }
    };

    const revealAfterFirstFrame = function () {
      if (typeof video.requestVideoFrameCallback === 'function') {
        video.requestVideoFrameCallback(function () {
          revealVideo();
        });
        return;
      }
      revealVideo();
    };

    const startPlayback = function () {
      video.play().then(revealAfterFirstFrame).catch(function () {});
    };

    if (video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
      startPlayback();
      return;
    }

    video.addEventListener('canplay', startPlayback, { once: true });
    video.load();
  }

  /** @param {Element} root */
  function getActiveSlide(root) {
    const slides = root.querySelectorAll('slideshow-slide, [id^="Slide-"]');
    for (let i = 0; i < slides.length; i++) {
      const slide = slides[i];
      if (slide.getAttribute('aria-hidden') === 'false') return slide;
      if (slide.classList.contains('is-active')) return slide;
    }

    if (root.id && root.id.indexOf('HeroSlider-') === 0 && slides.length) {
      const slider = root.querySelector('[id^="Slider-"]');
      if (slider) {
        const w = slider.offsetWidth || 1;
        const idx = Math.min(Math.max(0, Math.round(slider.scrollLeft / w)), slides.length - 1);
        return slides[idx];
      }
    }

    return slides[0] || null;
  }

  /** @param {Element} root */
  function syncVideos(root) {
    // Mobile: wait until LCP so poster remains the LCP candidate.
    if (isMobile() && !lcpReady) return;

    const activeSlide = getActiveSlide(root);
    const slideNodes = root.querySelectorAll('slideshow-slide, [id^="Slide-"]');

    if (slideNodes.length) {
      slideNodes.forEach(function (slide) {
        const video = getViewportVideo(slide);
        if (slide === activeSlide) {
          loadVideo(video);
        } else {
          unloadVideo(video);
        }
      });
      return;
    }

    loadVideo(getViewportVideo(root));
  }

  /** @param {Element} root */
  function initRoot(root) {
    if (root.getAttribute('data-hog-video-init') === 'true') return;
    root.setAttribute('data-hog-video-init', 'true');

    const run = function () {
      const activeSlide = getActiveSlide(root);
      hydrateActiveDesktopPoster(activeSlide);
      hydrateNextSlidePosters(root, activeSlide);
      syncVideos(root);
    };

    const defer = function () {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(run, { timeout: 2500 });
      } else {
        setTimeout(run, 1500);
      }
    };

    // Desktop: hydrate active only-desktop poster before idle (pairs with Link preload).
    hydrateActiveDesktopPoster(getActiveSlide(root));
    defer();

    // Mobile: also sync once LCP has fired (idle may run before LCP).
    afterLcp(function () {
      if (isMobile()) syncVideos(root);
    });

    root.addEventListener('slideshow:select', function (event) {
      const slide = event.detail && event.detail.slide;
      if (!slide) return;

      hydrateSlidePosters(slide);
      hydrateNextSlidePosters(root, slide);

      // Mobile: posters hydrate immediately; video waits for LCP.
      if (isMobile() && !lcpReady) return;

      root.querySelectorAll('slideshow-slide, [id^="Slide-"]').forEach(function (s) {
        const video = getViewportVideo(s);
        if (s === slide) {
          loadVideo(video);
        } else {
          unloadVideo(video);
        }
      });
    });

    MOBILE_QUERY.addEventListener('change', function () {
      root.querySelectorAll('.hog-deferred-video').forEach(function (video) {
        video.removeAttribute('data-loaded');
        unloadVideo(video);
      });
      defer();
      if (isMobile()) {
        afterLcp(function () {
          syncVideos(root);
        });
      }
    });

    if (root.id && root.id.indexOf('HeroSlider-') === 0) {
      const slider = root.querySelector('[id^="Slider-"]');
      if (slider) {
        let scrollTimer = null;
        slider.addEventListener('scroll', function () {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(function () {
            syncVideos(root);
          }, 60);
        });
        slider.querySelectorAll('[id^="Slide-"]').forEach(function (slide) {
          new MutationObserver(function () {
            syncVideos(root);
          }).observe(slide, { attributes: true, attributeFilter: ['class'] });
        });
      }
    }
  }

  function init() {
    initLcpGate();
    document.querySelectorAll('slideshow-component').forEach(initRoot);
    document.querySelectorAll('[id^="HeroSlider-"]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
